import { useMemo, useState } from 'react';

import { Box, Button, Icon, TextButton } from '@zillow/constellation-10';
import { IconProfileFilled } from '@zillow/constellation-icons';

import { useRentalManagerUserPropertySummaryWebApiHandler_applications } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import selectPropertyUtils from '../../../../../srcLegacy/shared/utils/selectPropertyUtils';

import { PERMISSION_REQUEST_TYPE } from '../../../../../srcLegacy/shared/constants/ApplicationPermissionsConstants';
import { VERTICAL_DISPLAY_TYPE } from '../../../../../srcLegacy/shared/constants/PropertiesConstants';
import SelectPropertyConstants from '../../../../../srcLegacy/shared/constants/SelectPropertyConstants';
import TimeConstants from '../../../../../srcLegacy/shared/constants/TimeConstants';

import ConnectedModuleCard from '../../../../../srcLegacy/shared/components/ConnectedModuleCard';
import EmptyState from '../../../../../srcLegacy/shared/components/EmptyState';
import ApplicationItem from '../../../ApplicationsPage/components/ApplicationItem';
import ApplicationPermissionCard from '../../../ApplicationsPage/components/ApplicationPermissionCard';
import ApplicationsSummaryCard from '../../../ApplicationsPage/components/ApplicationsSummaryCard';
import ApplicationsPermissionSetupModal from '../../../modals/ApplicationsPermissionSetupModal';
import InviteToApplyModal from './InviteToApplyModal';

import type { Application } from '../../../ApplicationsPage/components/ApplicationItem';

type Props = {
  listingAlias: string;
  street?: string;
  propertyAddress?: { street?: string; city?: string; state?: string; zip?: string; unit?: string };
};

const ApplicationsModule = ({ listingAlias, street, propertyAddress }: Props) => {
  const [isInviteToApplyModalOpen, setIsInviteToApplyModalOpen] = useState(false);
  const [showAppsPermissionModal, setShowAppsPermissionModal] = useState(false);

  const { data, isPending } = useRentalManagerUserPropertySummaryWebApiHandler_applications(
    { listingAlias, getActive: true },
    { enabled: Boolean(listingAlias), staleTime: TimeConstants.SECONDS_10 },
  );

  const applicationsPath = selectPropertyUtils.getPathByType(
    SelectPropertyConstants.APPLICATIONS as keyof typeof SelectPropertyConstants,
    listingAlias,
  );

  const renderCTA = useMemo(() => {
    return (
      <TextButton tone="brand" asChild>
        <a href={applicationsPath || ''} target="_self">
          Details
        </a>
      </TextButton>
    );
  }, [applicationsPath]);

  const applicationList = data?.rentalApplicationData?.applicationList ?? [];
  const totalApplications = data?.rentalApplicationData?.totalNumberOfActiveApplications ?? 0;
  const hasApplications = totalApplications > 0;
  const isApplicationsAccepted = data?.isApplicationsAccepted ?? false;
  const isInviteOnly = data?.inviteOnly?.isInviteOnly ?? false;

  // Check user management flags for permission restrictions
  const userManagementFlags = data?.userManagementFlags;
  const isEligible = userManagementFlags?.isEligible ?? false;
  const canEnableApplications = userManagementFlags?.canEnableApplications ?? false;
  const canRequestAccess = userManagementFlags?.canRequestAccess ?? false;
  const isApplicationPermissionsEnabledForCompany =
    userManagementFlags?.isApplicationPermissionsEnabledForCompany ?? false;

  // Determine if user has permission restrictions (matches legacy logic)
  const hasNoPermission = isEligible && !canEnableApplications && !canRequestAccess;
  const needsToRequestAccess = isEligible && isApplicationPermissionsEnabledForCompany && canRequestAccess;
  const needsToSetApplicationManager =
    isEligible && !isApplicationPermissionsEnabledForCompany && canEnableApplications;

  const showSummaryCard = totalApplications > 4;
  const applicationsToRender = showSummaryCard ? applicationList.slice(0, 3) : applicationList;

  const handleSetApplicationManagerClick = () => {
    setShowAppsPermissionModal(true);
  };

  // Determine permission type for ApplicationPermissionCard
  let permissionType: keyof typeof PERMISSION_REQUEST_TYPE | null = null;
  if (hasNoPermission) {
    permissionType = PERMISSION_REQUEST_TYPE.NO_PERMISSION;
  } else if (needsToRequestAccess) {
    permissionType = PERMISSION_REQUEST_TYPE.REQUEST_ACCESS;
  } else if (needsToSetApplicationManager) {
    permissionType = PERMISSION_REQUEST_TYPE.SET_APPLICATION_MANAGER;
  }

  const renderContent = () => {
    if (isPending) {
      return null;
    }

    // Check permission restrictions first (before checking if applications are disabled)
    if (permissionType) {
      return (
        <ApplicationPermissionCard
          permissionType={permissionType}
          onSetApplicationManagerClick={handleSetApplicationManagerClick}
          applicationsSummary={data}
          listingAlias={listingAlias}
        />
      );
    }

    // Applications are disabled - show message to enable them
    if (!isApplicationsAccepted && !isInviteOnly) {
      return (
        <EmptyState
          data-testid="ApplicationsDisabledCard"
          useCard={true}
          align="left"
          headingLevel={4}
          icon={<Icon size="md" css={{ color: 'icon.subtle' }} render={<IconProfileFilled />} />}
          heading="Enable applications for your property"
          body="Allow renters to apply online. You’ll be able to view applications, request reports, and invite renters to apply."
          actions={
            <Button tone="brand" emphasis="filled" asChild>
              <a href={applicationsPath || ''} target="_self">
                Enable applications
              </a>
            </Button>
          }
        />
      );
    }

    // Applications are enabled but no applications yet
    if (!hasApplications) {
      return (
        <EmptyState
          data-testid="EmptyApplicationsCardSimple"
          useCard={true}
          align="left"
          headingLevel={4}
          icon={<Icon size="md" css={{ color: 'icon.subtle' }} render={<IconProfileFilled />} />}
          heading="You don’t have any applications yet"
          body="You can invite renters to apply or wait for renters to apply via your listing on Zillow Rental Network."
          actions={
            <Button tone="brand" emphasis="filled" onClick={() => setIsInviteToApplyModalOpen(true)}>
              Invite to apply
            </Button>
          }
        />
      );
    }

    // Has applications - show individual application cards
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: {
            base: 'repeat(1, 1fr)',
            lg: applicationList.length < 2 ? 'repeat(1, minmax(0,1fr))' : 'repeat(2, minmax(0,1fr))',
          },
          gap: { base: '200', lg: '400' },
        }}
      >
        {applicationsToRender.map((application: Application, idx: number) => {
          return (
            <Box key={`application-${idx}`}>
              <ApplicationItem
                application={application}
                listingAlias={listingAlias}
                isLoading={false}
                displayType={VERTICAL_DISPLAY_TYPE.SIMPLE}
              />
            </Box>
          );
        })}

        {showSummaryCard && (
          <Box>
            <ApplicationsSummaryCard applicationsSummary={data} displayType={VERTICAL_DISPLAY_TYPE.SIMPLE} />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <ConnectedModuleCard
      data-testid="ApplicationsModule"
      title="Applications"
      ctaComponent={!isPending && (isApplicationsAccepted || isInviteOnly) && renderCTA}
      ctaFontType="body-sm"
      justifyHeadingContent={'center'}
      isLoading={isPending}
    >
      {renderContent()}

      <InviteToApplyModal
        listingAlias={listingAlias}
        street={street}
        open={isInviteToApplyModalOpen}
        onOpenChange={setIsInviteToApplyModalOpen}
      />

      {showAppsPermissionModal && (
        <ApplicationsPermissionSetupModal
          setShowApplicationsPermissionSetupModal={setShowAppsPermissionModal}
          propertyAddress={propertyAddress || { street }}
        />
      )}
    </ConnectedModuleCard>
  );
};

export default ApplicationsModule;
